"use client";

import { useState } from "react";
import Link from "next/link";
import StrikeAuthMenu from "@/components/auth/StrikeAuthMenu";
import { useUser } from "@/components/UserContext";
import {
    useLanguage,
} from "@/components/LanguageContext";
import {
    getLocalizedPath,
} from "@/lib/localizedRoutes";
import {
    House,
    Crosshair,
    CalendarDays,
    UserRound,
    RotateCcw,
    LogOut,
    X,
} from "lucide-react";

const IS_DEVELOPMENT =
    process.env.NODE_ENV ===
    "developmente";

export default function MobileBottomNav() {
    const {
        language,
    } =
        useLanguage();

    const [
        authOpenTrigger,
        setAuthOpenTrigger,
    ] = useState(0);

    const [
        isProfileOpen,
        setIsProfileOpen,
    ] = useState(false);

    const {
        user,
        isUserLoading,
        clearUser,
    } = useUser();

    if (!IS_DEVELOPMENT) {
        return null;
    }

    const handleProfileClick = () => {
        if (isUserLoading) {
            return;
        }

        if (user) {
            setIsProfileOpen(true);
            return;
        }

        setAuthOpenTrigger(
            Date.now()
        );
    };

    const handleLogout = async () => {
        try {
            const response =
                await fetch(
                    "/api/auth/logout",
                    {
                        method: "POST",
                        cache: "no-store",
                    }
                );

            const data =
                await response
                    .json()
                    .catch(() => ({
                        ok: false,
                    }));

            if (
                data?.ok !==
                true
            ) {
                console.error(
                    "Logout failed:",
                    data
                );

                return;
            }

            clearUser();
            setIsProfileOpen(false);
        } catch (logoutError) {
            console.error(
                "Logout error:",
                logoutError
            );
        }
    };

    return (
        <>
            <nav
                aria-label="Mobil navigáció"
                className="
                    fixed bottom-0 left-0 right-0
                    z-[99990]
                    md:hidden
                "
            >
                <div
                    className="
                        mx-auto
                        flex h-[74px]
                        max-w-[520px]
                        items-stretch
                        border-t
                        border-white/10
                        bg-black/95
                        px-2
                        pb-[env(safe-area-inset-bottom)]
                        shadow-[0_-12px_40px_rgba(0,0,0,0.45)]
                        backdrop-blur-xl
                    "
                >
                    <Link
                        href={getLocalizedPath(
                            "/",
                            language
                        )}
                        className="
                            flex flex-1
                            cursor-pointer
                            flex-col
                            items-center
                            justify-center
                            gap-1
                            text-white/55
                            transition
                            active:scale-95
                        "
                    >
                        <House
                            size={22}
                            strokeWidth={2.3}
                        />

                        <span className="text-[10px] font-bold">
                            főoldal
                        </span>
                    </Link>

                    <Link
                        href={getLocalizedPath(
                            "/strike-zone",
                            language
                        )}
                        className="
                            flex flex-1
                            cursor-pointer
                            flex-col
                            items-center
                            justify-center
                            gap-1
                            text-white/55
                            transition
                            active:scale-95
                        "
                    >
                        <Crosshair
                            size={22}
                            strokeWidth={2.3}
                        />

                        <span className="text-[10px] font-bold">
                            játék
                        </span>
                    </Link>

                    <Link
                        href="/booking"
                        className="
                            relative flex flex-1
                            cursor-pointer
                            flex-col
                            items-center
                            justify-center
                            gap-1
                            text-[#f5a400]
                            transition
                            active:scale-95
                        "
                    >
                        <div
                            className="
                                button-pulse absolute
                                -top-[15px]
                                flex h-[48px]
                                w-[48px]
                                items-center
                                justify-center
                                rounded-full
                                border-[4px]
                                border-black
                                bg-[#f5a400]
                                text-black
                                shadow-[0_8px_25px_rgba(245,164,0,0.28)]
                            "
                        >
                            <CalendarDays
                                size={23}
                                strokeWidth={2.5}
                            />
                        </div>

                        <span className="mt-[28px] text-[10px] font-black">
                            foglalás
                        </span>
                    </Link>

                    <button
                        type="button"
                        onClick={
                            handleProfileClick
                        }
                        disabled={
                            isUserLoading
                        }
                        className="
                            flex flex-1
                            cursor-pointer
                            flex-col
                            items-center
                            justify-center
                            gap-1
                            text-white/55
                            transition
                            active:scale-95
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                        "
                    >
                        <UserRound
                            size={22}
                            strokeWidth={2.3}
                        />

                        <span className="text-[10px] font-bold">
                            profil
                        </span>
                    </button>
                </div>
            </nav>

            <div className="hidden">
                <StrikeAuthMenu
                    openTrigger={
                        authOpenTrigger
                    }
                />
            </div>

            {isProfileOpen &&
                user && (
                    <div
                        className="
                            fixed inset-0
                            z-[999999]
                            flex items-end
                            bg-black/70
                            backdrop-blur-sm
                            md:hidden
                        "
                        onClick={() => {
                            setIsProfileOpen(false);
                        }}
                    >
                        <div
                            className="
                                relative
                                w-full
                                border-t-2
                                border-[#f5a400]
                                bg-[#0b0b0b]
                                px-5
                                pb-[calc(24px+env(safe-area-inset-bottom))]
                                pt-5
                                text-white
                                shadow-[0_-25px_80px_rgba(0,0,0,0.75)]
                            "
                            onClick={(event) => {
                                event.stopPropagation();
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    setIsProfileOpen(false);
                                }}
                                aria-label="Profil bezárása"
                                className="
                                    absolute
                                    right-4 top-4
                                    flex h-10 w-10
                                    cursor-pointer
                                    items-center
                                    justify-center
                                    rounded-full
                                    border
                                    border-white/10
                                    text-white/60
                                    transition
                                    hover:border-[#f5a400]
                                    hover:text-[#f5a400]
                                    active:scale-95
                                "
                            >
                                <X
                                    size={21}
                                    strokeWidth={2.4}
                                />
                            </button>

                            <div
                                className="
                                    flex
                                    items-center
                                    gap-4
                                    pr-12
                                "
                            >
                                <div
                                    className="
                                        flex h-12 w-12
                                        shrink-0
                                        items-center
                                        justify-center
                                        rounded-full
                                        bg-[#f5a400]
                                        text-black
                                    "
                                >
                                    <UserRound
                                        size={24}
                                        strokeWidth={2.5}
                                    />
                                </div>

                                <div className="min-w-0">
                                    <p
                                        className="
                                            text-[10px]
                                            font-black
                                            uppercase
                                            tracking-[0.16em]
                                            text-white/40
                                        "
                                    >
                                        bejelentkezve mint
                                    </p>

                                    <p
                                        className="
                                            mt-1
                                            truncate
                                            text-[18px]
                                            font-black
                                            text-white
                                        "
                                    >
                                        {user.username ||
                                            user.email ||
                                            "felhasználó"}
                                    </p>
                                </div>
                            </div>

                            <div
                                className="
                                    mt-5
                                    space-y-3
                                    border-t
                                    border-white/10
                                    pt-4
                                "
                            >

                                <button
                                    type="button"
                                    onClick={
                                        handleLogout
                                    }
                                    className="
                                        flex min-h-[52px]
                                        w-full
                                        cursor-pointer
                                        items-center
                                        justify-center
                                        gap-3
                                        border
                                        border-white/10
                                        bg-white/[0.04]
                                        px-4
                                        text-sm
                                        font-black
                                        text-white
                                        transition
                                        hover:border-[#f5a400]
                                        hover:text-[#f5a400]
                                        active:scale-[0.98]
                                    "
                                >
                                    <LogOut
                                        size={19}
                                        strokeWidth={2.4}
                                    />

                                    kijelentkezés
                                </button>
                            </div>
                        </div>
                    </div>
                )}
        </>
    );
}